/*
 * Purpose: Authentic split-flap cell — center-hinged top/bottom halves (Solari/Pragotron)
 * Ref: https://en.wikipedia.org/wiki/Split-flap_display
 * Last modified: 2026-06-27 | Agent
 * Completeness: 99
 */
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const STAGGER_MS = 36;
const BOTTOM_FLIP_MS = 220;
const TOP_FLIP_MS = 220;
const FLIP_EASE = [0.42, 0.02, 0.38, 1] as const;

function FlapHalf({ char, half }: { char: string; half: 'top' | 'bottom' }) {
  return (
    <span className={`ab-flap-half ab-flap-half--${half}`}>
      <span className="ab-flap-glyph-wrap">{char}</span>
    </span>
  );
}

export function FlapIdleChar({ char }: { char: string }) {
  return <span className="ab-flap-idle">{char}</span>;
}

export interface SplitFlapSlotProps {
  char: string;
  slotIndex: number;
  /** Column within the row (for trailing pad detection) */
  col: number;
  /** Phrase index — changes when headline rotates */
  tick: number;
  /** Visible character count for this row (excludes trailing pad) */
  rowVisibleLength: number;
}

type FlipPhase = 'idle' | 'bottom' | 'top';

export function SplitFlapSlot({
  char,
  slotIndex,
  col,
  tick,
  rowVisibleLength,
}: SplitFlapSlotProps) {
  const glyph = char === ' ' ? '' : char;
  const isTrailingPad = char === ' ' && col >= rowVisibleLength;
  const isWordGap = char === ' ' && !isTrailingPad;

  const [displayChar, setDisplayChar] = useState(glyph);
  const [targetChar, setTargetChar] = useState(glyph);
  const [phase, setPhase] = useState<FlipPhase>('idle');
  const [flipKey, setFlipKey] = useState(0);
  const booted = useRef(false);
  const displayRef = useRef(glyph);
  const phaseRef = useRef<FlipPhase>('idle');
  const flipGen = useRef(0);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    displayRef.current = displayChar;
  }, [displayChar]);

  useEffect(() => {
    if (!booted.current) {
      booted.current = true;
      displayRef.current = glyph;
      setDisplayChar(glyph);
      setTargetChar(glyph);
      return;
    }

    if (glyph === displayRef.current) return;

    const gen = ++flipGen.current;
    setTargetChar(glyph);
    setPhase('idle');
    setFlipKey(gen);

    const delayTimer = window.setTimeout(() => {
      if (flipGen.current !== gen) return;
      setPhase('bottom');
    }, slotIndex * STAGGER_MS);

    return () => window.clearTimeout(delayTimer);
  }, [glyph, tick, slotIndex]);

  const finishFlip = () => {
    displayRef.current = targetChar;
    setDisplayChar(targetChar);
    setPhase('idle');
  };

  if (isTrailingPad) {
    return <span className="ab-flip-pad" aria-hidden />;
  }

  if (isWordGap && phase === 'idle') {
    return <span className="ab-flip-gap" aria-hidden />;
  }

  const flipping = phase !== 'idle';

  return (
    <span
      className={`ab-flip-cell${isWordGap ? ' ab-flip-cell--gap' : ''}`}
      aria-hidden
    >
      <span className="ab-flip-cell__hinge" aria-hidden />

      <span className="ab-flap-stage">
        {flipping ? (
          <span key={flipKey} className="ab-flap-flip-stack">
            <span className="ab-flap-rear" aria-hidden>
              <FlapHalf char={targetChar} half="top" />
              <FlapHalf char={targetChar} half="bottom" />
            </span>
            <motion.span
              className="ab-flap-rotor ab-flap-rotor--bottom"
              initial={{ rotateX: 0 }}
              animate={{ rotateX: 90 }}
              transition={{ duration: BOTTOM_FLIP_MS / 1000, ease: FLIP_EASE }}
              onAnimationComplete={() => {
                if (phaseRef.current === 'bottom') setPhase('top');
              }}
            >
              <FlapHalf char={displayChar} half="bottom" />
            </motion.span>
            <motion.span
              className="ab-flap-rotor ab-flap-rotor--top"
              initial={{ rotateX: 0 }}
              animate={{ rotateX: phase === 'top' ? -90 : 0 }}
              transition={{ duration: TOP_FLIP_MS / 1000, ease: FLIP_EASE }}
              onAnimationComplete={() => {
                if (phaseRef.current === 'top') finishFlip();
              }}
            >
              <FlapHalf char={displayChar} half="top" />
            </motion.span>
          </span>
        ) : (
          <FlapIdleChar char={displayChar} />
        )}
      </span>
    </span>
  );
}

export function splitFlapPhraseDurationMs(charCount: number): number {
  return charCount * STAGGER_MS + BOTTOM_FLIP_MS + TOP_FLIP_MS + 1600;
}
